
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { Language } from '@/types/language';
import { getLocalizedPath } from '@/utils/languageUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import '@/styles/emoji-flags.css';

const LanguagePopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    
  ];

  const welcomeTexts = [
    { text: 'Welcome', lang: 'en' },
    { text: 'Bienvenido', lang: 'es' },
    { text: 'Benvenuto', lang: 'it' },
    { text: 'Willkommen', lang: 'de' },
    
    { text: 'Bienvenue', lang: 'fr' },
    { text: 'أهلاً وسهلاً', lang: 'ar' },
    { text: 'Merhaba', lang: 'tr' },
    { text: '欢迎', lang: 'zh' },
    { text: 'Добро пожаловать', lang: 'ru' },
  ];

  useEffect(() => {
    // Check if language preference is stored
    const savedLang = localStorage.getItem('language');
    if (!savedLang) {
      setIsVisible(true);
    }
  }, []);

  const handleLanguageSelect = (langCode: string) => {
    const lang = langCode as Language;
    setLanguage(lang);
    
    // Update URL to reflect language change
    const newPath = getLocalizedPath(location.pathname, lang, location.search);
    navigate(newPath, { replace: true });
    
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 max-w-xs w-full mx-4 shadow-2xl relative overflow-hidden">
        <div className="text-center mb-3 relative z-10">
          <img 
            src="/logo_1_inverted_no_background.png" 
            alt="The Club Logo" 
            className="h-10 w-auto mx-auto my-3" 
            fetchPriority="high"
            onLoad={() => setLogoLoaded(true)}
          />
        </div>

        <div className={`transition-opacity duration-500 ${logoLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="mb-4">
            <div className="grid grid-cols-3 gap-1 text-center">
              {welcomeTexts.map((welcome, index) => (
                <div
                  key={index}
                  className={`text-gray-400/70 font-bold text-xs py-1 px-1 ${
                    welcome.lang === 'ar' ? 'font-arabic' : 'font-default'
                  }`}
                >
                  {welcome.text}
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-2 relative z-10">
            {languages.map((lang) => (
              <Button
                key={lang.code}
                variant="ghost"
                className="w-full justify-center text-left py-3 bg-gray-800/50 hover:bg-purple-600/20 border border-gray-700/50 hover:border-purple-500/50 text-white hover:text-white transition-all duration-200"
                onClick={() => handleLanguageSelect(lang.code)}
              >
                <span className="emoji-flag text-lg mr-2">{lang.flag}</span>
                <span className="text-sm">{lang.name}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguagePopup;
